import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from "@angular/core";

import { StringHelper } from "@tylertech/shared-module";
import { CustomViewService } from "@tylertech/custom-view";
import { AggregateCriteria, ICardData } from "@tylertech/tyler-api-types";

import { Aggregate, AggregateBindingObject, IActionPerformedEventArgs } from "../../interfaces";

@Component({
    selector: "aggregate-collection",
    templateUrl: "./aggregateCollection.tmpl.html",
    standalone: false
})
export class AggregateCollectionComponent implements OnInit, OnChanges
{
    private readonly $customViewService = inject(CustomViewService);

    private readonly customAggregateMap = new Map<string, string>();

    private readonly customBindingObjectMap = new Map<string, AggregateBindingObject>();

    @Input() aggregateCriterias: AggregateCriteria[];
    @Input() aggregates: Aggregate[];

    @Output() readonly actionPerformedEvent = new EventEmitter<IActionPerformedEventArgs>();

    showCount: number;

    readonly defaultNumberToShow: number = 10;

    get Aggregates(): Aggregate[]
    {
        if (!this.aggregates || this.aggregates.length === 0)
        {
            return [];
        }
        else if (this.showMore)
        {
            return this.aggregates.slice(0, this.showCount);
        }
        return this.aggregates;
    }

    get HasCriteria(): boolean
    {
        return !this.aggregates && !!this.aggregateCriterias;
    }

    get showMore(): boolean
    {
        return (
            !!this.aggregates &&
            this.showCount > 0 &&
            this.aggregates.length > this.showCount
        );
    }

    ngOnInit(): void
    {
        this.setShowCount(this.defaultNumberToShow);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (this.aggregates)
        {
            this.setShowCount(this.aggregates.length);
        }
    }

    getBindingObject(criterion: Aggregate | AggregateCriteria): AggregateBindingObject
    {
        const key = this.isAggregate(criterion) ? criterion.AggregateProviderId : criterion?.ProviderId;
        if (!this.customBindingObjectMap.has(key))
        {
            this.customBindingObjectMap.set(
                key,
                this.createBindingObject(
                    this.isAggregate(criterion) ? criterion : undefined,
                    !this.isAggregate(criterion) ? criterion : undefined));
        }
        return this.customBindingObjectMap.get(key);
    }

    private createBindingObject(
        aggregate: Aggregate | undefined,
        aggregateCriteria: AggregateCriteria | undefined): AggregateBindingObject
    {
        return {
            aggregate,
            aggregateCriteria
        };
    }

    getCustomView(criterion: Aggregate | AggregateCriteria): string
    {
        const key = this.isAggregate(criterion) ? criterion.AggregateProviderId : criterion?.ProviderId;
        if (!this.customAggregateMap.has(key))
        {
            this.customAggregateMap.set(key, StringHelper.toKebabCase(key.replace(/\./, "")));
        }
        return this.customAggregateMap.get(key);
    }

    handleCardAction(actionPerformedOn: ICardData, action: string, replacement: ICardData): void
    {
        if (this.actionPerformedEvent)
        {
            this.actionPerformedEvent.emit(<IActionPerformedEventArgs>{
                card: actionPerformedOn,
                action,
                replacement: replacement,
            });
        }
    }

    handleShowMore($event?: MouseEvent): void
    {
        if ($event !== undefined)
        {
            $event.stopPropagation();
        }
        this.setShowCount(this.showCount + this.defaultNumberToShow);
    }

    private isAggregate(criterion: unknown): criterion is Aggregate
    {
        return criterion && typeof criterion === "object" && "AggregateProviderId" in criterion;
    }

    isCustomView(criterion: Aggregate | AggregateCriteria): boolean
    {
        const selector = this.getCustomView(criterion);
        return this.$customViewService.hasView(selector);
    }

    private setShowCount(showCount: number): void
    {
        this.showCount = showCount;
        if (this.aggregates && this.aggregates.length < this.showCount)
        {
            this.showCount = this.aggregates.length;
        }
    }

    trackByCriterion(_index: number, criteria: AggregateCriteria): string
    {
        return `criteria:${criteria.ProviderId}`;
    }

    trackByAggregate(_index: number, agg: Aggregate): string
    {
        return `${agg.AggregateProviderId}:${agg.Id}`;
    }
}
